import React, { useEffect, useState } from "react";
import { C, UNDO_WINDOW_SECONDS } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch } from "../api.js";
import { Badge } from "../components/Badge.jsx";
import { Spinner } from "../components/Spinner.jsx";

export function CrashReportCard({ matterId, clientName, showNotification, previouslyPushed, previousPushDetail, onWorkflowUpdate }) {
  const [step, setStep] = useState("idle"); // idle | scanning | select | extracting | review | pushing | generating | done
  const [pdfs, setPdfs] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [clientUnit, setClientUnit] = useState(0);
  const [pushOptions, setPushOptions] = useState({
    update_client_contact: true,
    update_custom_fields: true,
  });
  const [pushResult, setPushResult] = useState(null);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [summaryFilename, setSummaryFilename] = useState(null);
  // Undo-upload window for the generated summary doc. Same 30s window
  // pattern as the GeneratePage result rows — see UNDO_WINDOW_SECONDS.
  // Local state (not shared with MatterDetailPage's genResults) since
  // crash-summary lives in its own card with its own success surface.
  const [summaryDocId, setSummaryDocId] = useState(null);
  const [summaryGeneratedAt, setSummaryGeneratedAt] = useState(null);
  const [summaryUndone, setSummaryUndone] = useState(false);
  const [undoingSummary, setUndoingSummary] = useState(false);
  const [undoTick, setUndoTick] = useState(Date.now());
  const [error, setError] = useState(null);

  const scanForReports = async () => {
    setStep("scanning");
    setError(null);
    setPdfs([]);
    try {
      const data = await apiFetch(`/api/matters/${matterId}/crash-reports`);
      const found = data.data || [];
      if (found.length === 0) {
        const msg = data.folder_found
          ? "No PDFs in the Accident Report folder or the matter root."
          : "No '5 - Accident Report' folder found and no PDFs in the matter root.";
        setError(msg);
        showNotification(msg, "warning");
        setStep("idle");
        return;
      }
      if (data.source === "matter_root" && data.message) {
        // Fallback search — surface a soft warning, not an error
        showNotification(data.message, "warning");
      }
      setPdfs(found);
      // If there's only ONE candidate, skip the picker and auto-extract.
      // Backend already filters to positive content matches, so a single
      // result is unambiguous.
      if (found.length === 1) {
        await extractReport(found[0]);
        return;
      }
      setStep("select");
    } catch (err) {
      setError(err.message);
      showNotification("Failed to scan for crash reports: " + err.message, "error");
      setStep("idle");
    }
  };

  const extractReport = async (pdf) => {
    setSelectedPdf(pdf);
    setStep("extracting");
    setError(null);
    setExtraction(null);
    try {
      const data = await apiFetch(`/api/matters/${matterId}/crash-reports/extract`, {
        method: "POST",
        body: JSON.stringify({ document_id: pdf.id }),
      });
      setExtraction(data);
      setClientUnit(data.suggested_client_unit || 0);
      setStep("review");
      showNotification("Crash report extracted — review the data below and push to Clio when ready", "success");
    } catch (err) {
      setError(err.message);
      setStep("select");
      showNotification("Crash report extraction failed: " + err.message, "error");
    }
  };

  const pushToClio = async () => {
    setStep("pushing");
    setError(null);
    try {
      const data = await apiFetch(`/api/matters/${matterId}/crash-reports/push-to-clio`, {
        method: "POST",
        body: JSON.stringify({
          extraction: {
            crash: extraction.crash,
            units: extraction.units,
            victims: extraction.victims,
            narrative: extraction.narrative,
          },
          client_unit_index: clientUnit,
          ...pushOptions,
        }),
      });
      setPushResult(data);
      setStep("done");

      const actions = [];
      if (data.contact_updated) actions.push("client contact updated");
      if (data.custom_fields_updated?.length) actions.push(`${data.custom_fields_updated.length} custom fields set`);
      const hasNewWork = data.contact_updated || data.custom_fields_updated?.length;

      // Show warnings (name mismatch safety)
      if (data.warnings?.length) {
        data.warnings.forEach(w => showNotification(w, "warning"));
      }

      if (hasNewWork) {
        showNotification("Crash report data pushed: " + actions.join(", "), "success");
      } else if (!data.warnings?.length) {
        showNotification("No changes were made — all options were unchecked.", "warning");
      }
      onWorkflowUpdate && onWorkflowUpdate();
    } catch (err) {
      setError(err.message);
      setStep("review");
    }
  };

  const generateSummary = async () => {
    setStep("generating");
    setError(null);
    try {
      const data = await apiFetch(`/api/matters/${matterId}/crash-reports/generate-summary`, {
        method: "POST",
        body: JSON.stringify({
          extraction: {
            crash: extraction.crash,
            units: extraction.units,
            victims: extraction.victims,
            narrative: extraction.narrative,
          },
          client_unit_index: clientUnit,
          // Source PDF's Clio document_id — lets the backend re-download
          // and embed page 1 (where the diagram lives) into the summary.
          document_id: selectedPdf?.id,
        }),
      });
      setSummaryGenerated(true);
      setSummaryFilename(data.filename);
      setSummaryDocId(data.clio_document_id || null);
      setSummaryGeneratedAt(Date.now());
      setSummaryUndone(false);
      setStep("review");
      const folderMsg = data.folder ? ` in "${data.folder}"` : " (matter root)";
      showNotification(`Summary uploaded: ${data.filename}${folderMsg}`, "success");
      onWorkflowUpdate && onWorkflowUpdate();
    } catch (err) {
      setError(err.message);
      setStep("review");
      showNotification("Failed to generate summary: " + err.message, "error");
    }
  };

  const reset = () => {
    setStep("idle");
    setPdfs([]);
    setSelectedPdf(null);
    setExtraction(null);
    setClientUnit(0);
    setPushResult(null);
    setSummaryGenerated(false);
    setSummaryFilename(null);
    setSummaryDocId(null);
    setSummaryGeneratedAt(null);
    setSummaryUndone(false);
    setError(null);
  };

  // Tick once a second while the summary undo window is open so the
  // countdown updates. Stops once window closes.
  useEffect(() => {
    if (!summaryGeneratedAt || summaryUndone) return;
    const remaining = UNDO_WINDOW_SECONDS - Math.floor((Date.now() - summaryGeneratedAt) / 1000);
    if (remaining <= 0) return;
    const id = setInterval(() => setUndoTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [summaryGeneratedAt, summaryUndone]);

  const summaryUndoSecondsRemaining = summaryGeneratedAt
    ? Math.max(0, UNDO_WINDOW_SECONDS - Math.floor((undoTick - summaryGeneratedAt) / 1000))
    : 0;

  const handleUndoSummary = async () => {
    if (!summaryDocId) return;
    setUndoingSummary(true);
    try {
      await apiFetch(`/api/clio/documents/${summaryDocId}`, { method: "DELETE" });
      setSummaryUndone(true);
      showNotification(
        "Upload undone — the summary was removed from Clio.",
        "warning",
      );
      // Also reset the "Generate Summary" button so the user can
      // regenerate cleanly if they want to.
      setSummaryGenerated(false);
      onWorkflowUpdate && onWorkflowUpdate();
    } catch (e) {
      showNotification("Failed to undo summary upload: " + e.message, "error");
    } finally {
      setUndoingSummary(false);
    }
  };

  const cardStyle = { background:"#fff", borderRadius:"12px", padding:"20px 28px", border:`1px solid ${C.warmGray}`, marginBottom:"20px" };
  const headerStyle = { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"4px" };
  const sectionStyle = { padding:"14px 16px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, background:C.warmWhite, marginBottom:"12px" };
  const labelStyle = { fontSize:"11px", color:C.textLight, textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:600, marginBottom:"2px" };
  const valueStyle = { fontSize:"14px", color:C.navy, fontWeight:500 };

  // ---- IDLE STATE ----
  if (step === "idle") {
    return React.createElement("div", { style: { ...cardStyle, borderColor: previouslyPushed ? `${C.success}30` : cardStyle.borderColor } },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center" } },
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"10px" } },
          previouslyPushed && React.createElement("div", {
            style: { width:"32px", height:"32px", borderRadius:"50%", background:C.successBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }
          }, I.check),
          React.createElement("div", null,
            React.createElement("h2", { style: headerStyle }, "Crash Report"),
            previouslyPushed
              ? React.createElement("div", { style: { fontSize:"13px", color:C.success, fontWeight:500 } },
                  "Crash report data pushed to Clio",
                  previousPushDetail?.date && React.createElement("span", { style: { color:C.textLight, fontWeight:400 } },
                    " \u2014 " + new Date(previousPushDetail.date).toLocaleDateString()
                  ),
                )
              : React.createElement("div", { style: { fontSize:"13px", color:C.textLight } },
                  "Extract data from state crash reports (AL, GA, LA, FL, TN, MS)"
                ),
          ),
        ),
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px" } },
          previouslyPushed && React.createElement("span", {
            style: {
              padding:"4px 12px", borderRadius:"9999px", fontSize:"11px", fontWeight:600,
              background:C.successBg, color:C.success, letterSpacing:"0.03em",
            }
          }, "COMPLETE"),
          React.createElement("button", {
            onClick: scanForReports,
            style: {
              padding:"8px 20px", borderRadius:"8px", border: previouslyPushed ? `1px solid ${C.warmGray}` : "none",
              background: previouslyPushed ? "#fff" : C.navy, color: previouslyPushed ? C.textMuted : "#fff",
              fontSize:"13px", fontWeight:600,
              cursor:"pointer", display:"flex", alignItems:"center", gap:"6px",
            }
          }, I.search, previouslyPushed ? " Scan Again" : " Scan for Crash Reports"),
        ),
      ),
      error && React.createElement("div", {
        style: { marginTop:"10px", padding:"8px 12px", borderRadius:"6px", background:C.errorBg, color:C.error, fontSize:"13px" }
      }, error),
    );
  }

  // ---- SCANNING STATE ----
  if (step === "scanning") {
    return React.createElement("div", { style: cardStyle },
      React.createElement("h2", { style: headerStyle }, "Crash Report"),
      React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px", padding:"20px 0", justifyContent:"center" } },
        React.createElement(Spinner, { size:18 }),
        React.createElement("span", { style: { fontSize:"13px", color:C.textMuted } }, "Scanning accident report folder..."),
      ),
    );
  }

  // ---- SELECT PDF STATE ----
  if (step === "select") {
    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" } },
        React.createElement("h2", { style: headerStyle }, "Crash Report — Select PDF"),
        React.createElement("button", {
          onClick: reset,
          style: { border:"none", background:"none", fontSize:"13px", color:C.textMuted, cursor:"pointer", textDecoration:"underline" }
        }, "Cancel"),
      ),
      (() => {
        const fromRoot = pdfs.some(p => p.source === "matter_root");
        const msg = fromRoot
          ? `No PDFs in "5 - Accident Report" — scanned matter root (${pdfs.length} PDF${pdfs.length === 1 ? "" : "s"}). Likely crash reports are flagged.`
          : `Found ${pdfs.length} PDF(s) in the Accident Report folder. Select one to extract:`;
        return React.createElement("div", { style: { fontSize:"13px", color:C.textMuted, marginBottom:"12px" } }, msg);
      })(),
      pdfs.map(pdf =>
        React.createElement("div", {
          key: pdf.id,
          onClick: () => extractReport(pdf),
          style: {
            padding:"12px 16px", borderRadius:"8px",
            border: `1px solid ${pdf.likely_crash_report ? C.gold : C.warmGray}`,
            background: pdf.likely_crash_report ? `${C.gold}08` : C.warmWhite,
            marginBottom:"8px", cursor:"pointer",
            display:"flex", alignItems:"center", gap:"10px",
            transition:"border-color 0.15s",
          },
          onMouseOver: (e) => { e.currentTarget.style.borderColor = C.gold; },
          onMouseOut: (e) => { e.currentTarget.style.borderColor = pdf.likely_crash_report ? C.gold : C.warmGray; },
        },
          I.doc,
          React.createElement("div", { style: { flex:1, minWidth:0 } },
            React.createElement("div", { style: { fontSize:"14px", fontWeight:600, color:C.navy, display:"flex", alignItems:"center", gap:"8px" } },
              React.createElement("span", null, pdf.name),
              pdf.likely_crash_report && pdf.source === "matter_root" && React.createElement("span", {
                style: { fontSize:"10px", fontWeight:700, letterSpacing:"0.05em", color:C.gold, background:`${C.gold}18`, padding:"2px 6px", borderRadius:"4px" }
              }, "LIKELY"),
              pdf.source === "matter_root" && !pdf.likely_crash_report && React.createElement("span", {
                style: { fontSize:"10px", fontWeight:600, color:C.textLight, background:`${C.textLight}15`, padding:"2px 6px", borderRadius:"4px" }
              }, "MATTER ROOT"),
            ),
            pdf.created_at && React.createElement("div", { style: { fontSize:"11px", color:C.textLight, marginTop:"2px" } },
              "Uploaded " + new Date(pdf.created_at).toLocaleDateString()
            ),
          ),
        )
      ),
    );
  }

  // ---- EXTRACTING STATE ----
  if (step === "extracting") {
    return React.createElement("div", { style: cardStyle },
      React.createElement("h2", { style: headerStyle }, "Crash Report"),
      React.createElement("div", { style: { textAlign:"center", padding:"40px 20px" } },
        React.createElement(Spinner, { size:32 }),
        React.createElement("div", { style: { marginTop:"16px", fontSize:"15px", fontWeight:600, color:C.navy } },
          "Extracting crash report data..."
        ),
        React.createElement("div", { style: { marginTop:"6px", fontSize:"13px", color:C.textMuted } },
          `Processing "${selectedPdf?.name}" — this takes about 15 seconds`
        ),
        React.createElement("div", { style: { marginTop:"4px", fontSize:"12px", color:C.textLight } },
          "Each page is analyzed by AI for driver, vehicle, insurance, and crash details"
        ),
      ),
    );
  }

  // ---- REVIEW STATE ----
  if (step === "review" && extraction) {
    const crash = extraction.crash || {};
    const units = extraction.units || [];
    const victims = extraction.victims || [];

    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" } },
        React.createElement("h2", { style: headerStyle }, "Crash Report — Review Extracted Data"),
        React.createElement("button", {
          onClick: reset,
          style: { border:"none", background:"none", fontSize:"13px", color:C.textMuted, cursor:"pointer", textDecoration:"underline" }
        }, "Start Over"),
      ),

      // Crash Details Section
      crash.date && React.createElement("div", { style: sectionStyle },
        React.createElement("div", { style: { fontSize:"13px", fontWeight:700, color:C.navy, marginBottom:"10px" } }, "Crash Details"),
        React.createElement("div", { style: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:"10px" } },
          [
            { label:"Report #", value: crash.report_number },
            { label:"Date", value: crash.date },
            { label:"Time", value: crash.time },
            { label:"Location", value: crash.location },
            { label:"City", value: crash.city },
            { label:"County", value: crash.county },
            { label:"Officer", value: crash.investigating_officer },
            { label:"Department", value: crash.officer_department },
          ].filter(f => f.value).map((f, i) =>
            React.createElement("div", { key: i },
              React.createElement("div", { style: labelStyle }, f.label),
              React.createElement("div", { style: valueStyle }, f.value),
            )
          ),
        ),
      ),

      // Unit Selector
      units.length > 1 && React.createElement("div", { style: { ...sectionStyle, borderColor: C.gold + "60", background: C.gold + "08" } },
        React.createElement("div", { style: { fontSize:"13px", fontWeight:700, color:C.navy, marginBottom:"8px" } },
          "Which unit is your client?"
        ),
        units.map((unit, i) =>
          React.createElement("label", {
            key: i,
            style: {
              display:"flex", alignItems:"center", gap:"8px", padding:"8px 12px",
              borderRadius:"6px", marginBottom:"4px", cursor:"pointer",
              background: clientUnit === i ? C.gold + "15" : "transparent",
              border: clientUnit === i ? `1px solid ${C.gold}` : "1px solid transparent",
            },
          },
            React.createElement("input", {
              type: "radio", name: "client_unit",
              checked: clientUnit === i,
              onChange: () => setClientUnit(i),
              style: { accentColor: C.gold },
            }),
            React.createElement("span", { style: { fontSize:"14px", fontWeight:600, color:C.navy } },
              `Unit ${unit.unit_number || i + 1}: ${unit.driver_name || unit.driver_first_name + " " + unit.driver_last_name || "Unknown"}`
            ),
            React.createElement("span", { style: { fontSize:"12px", color:C.textMuted, marginLeft:"auto" } },
              [unit.vehicle_year, unit.vehicle_make, unit.vehicle_model].filter(Boolean).join(" ")
            ),
            i === extraction.suggested_client_unit && React.createElement(Badge, { variant:"gold" }, "Auto-matched"),
          )
        ),
      ),

      // Units Detail
      units.map((unit, i) =>
        React.createElement("div", { key: i, style: { ...sectionStyle, borderColor: i === clientUnit ? C.gold + "40" : C.warmGray } },
          React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px" } },
            React.createElement("span", { style: { fontSize:"13px", fontWeight:700, color:C.navy } },
              `Unit ${unit.unit_number || i + 1} — ${i === clientUnit ? "CLIENT" : "DEFENDANT"}`
            ),
            i === clientUnit
              ? React.createElement(Badge, { variant:"gold" }, "Client")
              : React.createElement(Badge, { variant:"navy" }, "Defendant"),
          ),
          React.createElement("div", { style: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:"8px" } },
            [
              { label:"Name", value: unit.driver_name || (unit.driver_first_name + " " + unit.driver_last_name).trim() },
              { label:"DOB", value: unit.driver_dob },
              { label:"Address", value: unit.driver_address },
              { label:"City/State/ZIP", value: [unit.driver_city, unit.driver_state, unit.driver_zip].filter(Boolean).join(", ") },
              { label:"Phone", value: unit.driver_phone },
              { label:"License", value: unit.driver_license_number },
              { label:"Vehicle", value: [unit.vehicle_year, unit.vehicle_make, unit.vehicle_model].filter(Boolean).join(" ") },
              { label:"Color", value: unit.vehicle_color },
              { label:"Insurance", value: unit.insurance_company },
              { label:"Policy #", value: unit.insurance_policy_number },
              { label:"Damage", value: unit.damage_extent },
              { label:"Injury", value: unit.driver_injury },
            ].filter(f => f.value).map((f, j) =>
              React.createElement("div", { key: j },
                React.createElement("div", { style: labelStyle }, f.label),
                React.createElement("div", { style: { fontSize:"13px", color:C.navy } }, f.value),
              )
            ),
          ),
        )
      ),

      // Victims
      victims.length > 0 && React.createElement("div", { style: sectionStyle },
        React.createElement("div", { style: { fontSize:"13px", fontWeight:700, color:C.navy, marginBottom:"10px" } }, "Victims / Injuries"),
        React.createElement("table", { style: { width:"100%", fontSize:"13px", borderCollapse:"collapse" } },
          React.createElement("thead", null,
            React.createElement("tr", { style: { borderBottom:`1px solid ${C.warmGray}`, textAlign:"left" } },
              ["Name","Unit","Injury","Hospital","EMS"].map(h =>
                React.createElement("th", { key:h, style: { padding:"6px 8px", fontSize:"11px", color:C.textMuted, fontWeight:600, textTransform:"uppercase" } }, h)
              )
            )
          ),
          React.createElement("tbody", null,
            victims.map((v, i) =>
              React.createElement("tr", { key:i, style: { borderBottom:`1px solid ${C.warmGray}15` } },
                React.createElement("td", { style: { padding:"6px 8px", color:C.navy, fontWeight:500 } }, v.name),
                React.createElement("td", { style: { padding:"6px 8px" } }, v.unit_number || ""),
                React.createElement("td", { style: { padding:"6px 8px" } }, v.injury_severity || ""),
                React.createElement("td", { style: { padding:"6px 8px" } }, v.transported_to || ""),
                React.createElement("td", { style: { padding:"6px 8px" } }, v.transported_by || ""),
              )
            )
          ),
        ),
      ),

      // Narrative
      extraction.narrative && React.createElement("div", { style: sectionStyle },
        React.createElement("div", { style: { fontSize:"13px", fontWeight:700, color:C.navy, marginBottom:"8px" } }, "Officer Narrative"),
        React.createElement("div", { style: { fontSize:"13px", color:C.textDark, lineHeight:1.7, whiteSpace:"pre-wrap" } },
          extraction.narrative
        ),
      ),

      // Push Options — detailed preview
      React.createElement("div", { style: { ...sectionStyle, borderColor: C.navy + "30" } },
        React.createElement("div", { style: { fontSize:"13px", fontWeight:700, color:C.navy, marginBottom:"12px" } }, "What to push to Clio:"),

        // --- Client Contact ---
        (() => {
          const cu = units[clientUnit] || {};
          const contactFields = [
            { label:"Name", value: [cu.driver_first_name, cu.driver_last_name].filter(Boolean).join(" ") || cu.driver_name },
            { label:"Date of Birth", value: cu.driver_dob },
            { label:"Phone", value: cu.driver_phone },
            { label:"Address", value: [cu.driver_address, cu.driver_city, cu.driver_state, cu.driver_zip].filter(Boolean).join(", ") },
          ].filter(f => f.value);
          return React.createElement("div", { style: { marginBottom:"12px" } },
            React.createElement("label", {
              style: { display:"flex", alignItems:"center", gap:"8px", cursor:"pointer", fontSize:"13px", fontWeight:600, color:C.navy },
            },
              React.createElement("input", {
                type: "checkbox", checked: pushOptions.update_client_contact,
                onChange: () => setPushOptions(prev => ({ ...prev, update_client_contact: !prev.update_client_contact })),
                style: { accentColor: C.gold },
              }),
              "Update Client Contact"
            ),
            React.createElement("div", { style: { fontSize:"11px", color:C.textLight, marginLeft:"24px", marginTop:"2px" } }, "Clio > Contacts > Client"),
            pushOptions.update_client_contact && contactFields.length > 0 && React.createElement("div", {
              style: { marginLeft:"24px", marginTop:"6px", padding:"8px 12px", borderRadius:"6px", background:"#f8f8f6", fontSize:"12px", color:C.textDark, display:"grid", gridTemplateColumns:"auto 1fr", gap:"2px 12px" },
            },
              contactFields.map((f, i) => [
                React.createElement("span", { key:f.label+"l", style: { color:C.textMuted, fontWeight:600 } }, f.label + ":"),
                React.createElement("span", { key:f.label+"v" }, f.value),
              ]).flat()
            ),
          );
        })(),

        // --- Custom Fields ---
        (() => {
          const cu = units[clientUnit] || {};
          const vehicle = [cu.vehicle_year, cu.vehicle_make, cu.vehicle_model].filter(Boolean).join(" ");
          const defUnit = units.find((u, i) => i !== clientUnit);
          const cfFields = [
            { label:"Date of Incident", value: crash.date },
            { label:"Location of Loss", value: [crash.location, crash.city, crash.county ? crash.county + " County" : ""].filter(Boolean).join(", ") },
            { label:"Accident Report Number", value: crash.report_number },
            { label:"Date of Birth", value: cu.driver_dob },
            { label:"Clients Vehicle: Year/Make/Model", value: vehicle },
            { label:"UM/Med Pay Insurer, Claim #/Adjuster Contact Info", value: cu.insurance_company },
            { label:"At-Fault Insurer/Claim #/Adjuster Contact Information", value: defUnit ? defUnit.insurance_company : "" },
          ].filter(f => f.value);
          return React.createElement("div", { style: { marginBottom:"12px" } },
            React.createElement("label", {
              style: { display:"flex", alignItems:"center", gap:"8px", cursor:"pointer", fontSize:"13px", fontWeight:600, color:C.navy },
            },
              React.createElement("input", {
                type: "checkbox", checked: pushOptions.update_custom_fields,
                onChange: () => setPushOptions(prev => ({ ...prev, update_custom_fields: !prev.update_custom_fields })),
                style: { accentColor: C.gold },
              }),
              "Set Matter Custom Fields"
            ),
            React.createElement("div", { style: { fontSize:"11px", color:C.textLight, marginLeft:"24px", marginTop:"2px" } }, "Clio > Matters > Custom Fields"),
            pushOptions.update_custom_fields && cfFields.length > 0 && React.createElement("div", {
              style: { marginLeft:"24px", marginTop:"6px", padding:"8px 12px", borderRadius:"6px", background:"#f8f8f6", fontSize:"12px", color:C.textDark, display:"grid", gridTemplateColumns:"auto 1fr", gap:"2px 12px" },
            },
              cfFields.map((f, i) => [
                React.createElement("span", { key:f.label+"l", style: { color:C.textMuted, fontWeight:600 } }, f.label + ":"),
                React.createElement("span", { key:f.label+"v" }, f.value),
              ]).flat()
            ),
          );
        })(),

        // --- Defendant Contact ---
        (() => {
          const defUnit = units.find((u, i) => i !== clientUnit);
          if (!defUnit) return null;
          const defFields = [
            { label:"Name", value: [defUnit.driver_first_name, defUnit.driver_last_name].filter(Boolean).join(" ") || defUnit.driver_name },
            { label:"Phone", value: defUnit.driver_phone },
            { label:"Address", value: [defUnit.driver_address, defUnit.driver_city, defUnit.driver_state, defUnit.driver_zip].filter(Boolean).join(", ") },
            { label:"Vehicle", value: [defUnit.vehicle_year, defUnit.vehicle_make, defUnit.vehicle_model].filter(Boolean).join(" ") },
          ].filter(f => f.value);
          return React.createElement("div", { style: { marginBottom:"12px" } },
            React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy } },
              "At-Fault Driver \u2192 custom field"
            ),
            React.createElement("div", { style: { fontSize:"11px", color:C.textLight, marginTop:"2px" } },
              "Saved to matter custom field \"At-Fault Driver Name\" \u2014 no Clio contact is created."
            ),
            defFields.length > 0 && React.createElement("div", {
              style: { marginTop:"6px", padding:"8px 12px", borderRadius:"6px", background:"#f8f8f6", fontSize:"12px", color:C.textDark, display:"grid", gridTemplateColumns:"auto 1fr", gap:"2px 12px" },
            },
              defFields.map((f, i) => [
                React.createElement("span", { key:f.label+"l", style: { color:C.textMuted, fontWeight:600 } }, f.label + ":"),
                React.createElement("span", { key:f.label+"v" }, f.value),
              ]).flat()
            ),
          );
        })(),

        // --- At-Fault Insurance ---
        (() => {
          const defUnit = units.find((u, i) => i !== clientUnit);
          const insName = defUnit ? defUnit.insurance_company : "";
          if (!insName) return null;
          return React.createElement("div", { style: { marginBottom:"4px" } },
            React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy } },
              "At-Fault Insurer \u2192 custom field"
            ),
            React.createElement("div", { style: { fontSize:"11px", color:C.textLight, marginTop:"2px" } },
              "Saved to matter custom field \"At-Fault Insurer Name\" \u2014 no Clio contact is created."
            ),
            React.createElement("div", {
              style: { marginTop:"6px", padding:"8px 12px", borderRadius:"6px", background:"#f8f8f6", fontSize:"12px", color:C.textDark, display:"grid", gridTemplateColumns:"auto 1fr", gap:"2px 12px" },
            },
              React.createElement("span", { style: { color:C.textMuted, fontWeight:600 } }, "Company:"),
              React.createElement("span", null, insName),
              defUnit.insurance_policy_number && React.createElement("span", { style: { color:C.textMuted, fontWeight:600 } }, "Policy #:"),
              defUnit.insurance_policy_number && React.createElement("span", null, defUnit.insurance_policy_number),
            ),
          );
        })(),
      ),

      // Summary Generated indicator. Includes an "Undo upload" button
      // for UNDO_WINDOW_SECONDS after a successful upload — same pattern
      // as the GeneratePage result rows. Once undone, the row visually
      // changes to a muted "Removed" state with strikethrough so the
      // user can clearly see the Clio doc is gone.
      summaryGenerated && React.createElement("div", {
        style: {
          padding:"10px 14px", borderRadius:"8px",
          background: summaryUndone ? C.warmGray + "40" : C.successBg,
          border: `1px solid ${summaryUndone ? C.textLight + "40" : C.success + "30"}`,
          marginBottom:"8px",
          display:"flex", alignItems:"center", gap:"8px",
        },
      },
        React.createElement("span", {
          style: { color: summaryUndone ? C.textMuted : C.success, flexShrink: 0 },
        }, summaryUndone ? I.back : I.check),
        React.createElement("span", {
          style: {
            fontSize:"13px", fontWeight:600,
            color: summaryUndone ? C.textMuted : C.success,
            flex: 1,
            textDecoration: summaryUndone ? "line-through" : "none",
          },
        }, summaryUndone
            ? `Summary removed: ${summaryFilename}`
            : `Summary uploaded: ${summaryFilename}`),
        !summaryUndone && summaryDocId && summaryUndoSecondsRemaining > 0 && React.createElement("button", {
          onClick: handleUndoSummary,
          disabled: undoingSummary,
          title: `Wrong matter? Click to delete this summary from Clio. Available for ${UNDO_WINDOW_SECONDS} seconds after generation.`,
          style: {
            fontSize:"11px", fontWeight:600,
            padding:"4px 10px", borderRadius:"6px",
            background: undoingSummary ? C.warmGray : "#fff",
            color: undoingSummary ? C.textMuted : C.error,
            border:`1px solid ${C.error}40`,
            cursor: undoingSummary ? "not-allowed" : "pointer",
            flexShrink: 0,
            opacity: undoingSummary ? 0.6 : 1,
          },
        },
          undoingSummary ? "Undoing…" : `Undo (${summaryUndoSecondsRemaining}s)`,
        ),
      ),

      // Action Buttons
      React.createElement("div", { style: { display:"flex", gap:"12px", marginTop:"8px", flexWrap:"wrap" } },
        React.createElement("button", {
          onClick: generateSummary,
          disabled: summaryGenerated,
          style: {
            padding:"10px 28px", borderRadius:"8px", border:"none",
            background: summaryGenerated ? C.textLight : C.gold, color:"#fff", fontSize:"14px", fontWeight:700,
            cursor: summaryGenerated ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:"6px",
            opacity: summaryGenerated ? 0.6 : 1,
          },
        }, I.doc, summaryGenerated ? " Summary Generated" : " Generate Summary"),
        React.createElement("button", {
          onClick: pushToClio,
          style: {
            padding:"10px 28px", borderRadius:"8px", border:`1px solid ${C.navy}`,
            background: C.navy, color:"#fff", fontSize:"14px", fontWeight:700,
            cursor:"pointer", display:"flex", alignItems:"center", gap:"6px",
          },
        }, I.zap, " Push to Clio"),
        React.createElement("button", {
          onClick: () => setStep("select"),
          style: {
            padding:"10px 20px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
            background:"#fff", color:C.textMuted, fontSize:"13px", fontWeight:600, cursor:"pointer",
          },
        }, "Back"),
      ),

      error && React.createElement("div", {
        style: { marginTop:"10px", padding:"8px 12px", borderRadius:"6px", background:C.errorBg, color:C.error, fontSize:"13px" }
      }, error),

      // Pages processed info
      extraction.pages_processed && React.createElement("div", {
        style: { marginTop:"10px", fontSize:"11px", color:C.textLight, textAlign:"right" }
      }, `${extraction.pages_processed} of ${extraction.total_pages} pages processed`
        + (extraction.errors?.length ? ` (${extraction.errors.length} errors)` : "")),
    );
  }

  // ---- GENERATING STATE ----
  if (step === "generating") {
    return React.createElement("div", { style: cardStyle },
      React.createElement("h2", { style: headerStyle }, "Crash Report"),
      React.createElement("div", { style: { textAlign:"center", padding:"40px 20px" } },
        React.createElement(Spinner, { size:32 }),
        React.createElement("div", { style: { marginTop:"16px", fontSize:"15px", fontWeight:600, color:C.navy } },
          "Generating crash report summary..."
        ),
        React.createElement("div", { style: { marginTop:"6px", fontSize:"13px", color:C.textMuted } },
          "Building Word document and uploading to Clio"
        ),
      ),
    );
  }

  // ---- PUSHING STATE ----
  if (step === "pushing") {
    return React.createElement("div", { style: cardStyle },
      React.createElement("h2", { style: headerStyle }, "Crash Report"),
      React.createElement("div", { style: { textAlign:"center", padding:"40px 20px" } },
        React.createElement(Spinner, { size:32 }),
        React.createElement("div", { style: { marginTop:"16px", fontSize:"15px", fontWeight:600, color:C.navy } },
          "Pushing crash report data to Clio..."
        ),
        React.createElement("div", { style: { marginTop:"6px", fontSize:"13px", color:C.textMuted } },
          "Updating client contact and setting matter custom fields"
        ),
      ),
    );
  }

  // ---- DONE STATE ----
  if (step === "done" && pushResult) {
    const newActions = [];
    const skippedItems = [];
    if (pushResult.contact_updated) newActions.push("Client contact updated");
    if (pushResult.custom_fields_updated?.length) newActions.push(`Custom fields set: ${pushResult.custom_fields_updated.join(", ")}`);
    if (pushResult.errors?.length) skippedItems.push(...pushResult.errors.map(e => `Warning: ${e}`));
    const hasNewWork = newActions.length > 0;
    const allSkipped = !hasNewWork && skippedItems.length > 0;

    return React.createElement("div", { style: cardStyle },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" } },
        React.createElement("h2", { style: headerStyle }, "Crash Report — Complete"),
        React.createElement("button", {
          onClick: reset,
          style: { border:"none", background:"none", fontSize:"13px", color:C.textMuted, cursor:"pointer", textDecoration:"underline" }
        }, "Process Another"),
      ),

      // Success section (new work done)
      hasNewWork && React.createElement("div", { style: { padding:"16px", borderRadius:"8px", background:C.successBg, border:`1px solid ${C.success}30`, marginBottom: skippedItems.length ? "10px" : 0 } },
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" } },
          I.check,
          React.createElement("span", { style: { fontSize:"14px", fontWeight:700, color:C.success } }, "Crash report data pushed to Clio"),
        ),
        newActions.map((s, i) =>
          React.createElement("div", { key:i, style: { fontSize:"13px", color:C.success, paddingLeft:"24px", marginBottom:"2px" } },
            "\u2022 " + s
          )
        ),
      ),

      // Already exists section
      allSkipped && React.createElement("div", { style: { padding:"16px", borderRadius:"8px", background:"#FEF3C7", border:"1px solid #F59E0B40" } },
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" } },
          I.alert,
          React.createElement("span", { style: { fontSize:"14px", fontWeight:700, color:"#92400E" } }, "Already pushed — no duplicate entries created"),
        ),
        React.createElement("div", { style: { fontSize:"13px", color:"#92400E", paddingLeft:"24px", marginBottom:"4px" } },
          "This crash report data was already pushed to Clio for this matter. The app detected the existing entries and skipped them to avoid duplicates."
        ),
        skippedItems.map((s, i) =>
          React.createElement("div", { key:i, style: { fontSize:"13px", color:"#92400E", paddingLeft:"24px", marginBottom:"2px" } },
            "\u2022 " + s
          )
        ),
      ),

      // Warnings (name mismatch safety)
      pushResult.warnings?.length > 0 && React.createElement("div", {
        style: { padding:"16px", borderRadius:"8px", background:"#FEF3C7", border:"1px solid #F59E0B40", marginTop:"10px" }
      },
        React.createElement("div", { style: { display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" } },
          I.alert,
          React.createElement("span", { style: { fontSize:"14px", fontWeight:700, color:"#92400E" } }, "Safety Warning"),
        ),
        pushResult.warnings.map((w, i) =>
          React.createElement("div", { key:i, style: { fontSize:"13px", color:"#92400E", paddingLeft:"24px", marginBottom:"4px", lineHeight:"1.5" } }, w)
        ),
      ),

      // Mixed: skipped items shown below success
      hasNewWork && skippedItems.length > 0 && React.createElement("div", { style: { padding:"12px 16px", borderRadius:"8px", background:"#FEF3C7", border:"1px solid #F59E0B40", marginTop:"10px" } },
        skippedItems.map((s, i) =>
          React.createElement("div", { key:i, style: { fontSize:"13px", color:"#92400E" } },
            "\u2022 Skipped: " + s
          )
        ),
      ),
    );
  }

  // Fallback
  return React.createElement("div", { style: cardStyle },
    React.createElement("h2", { style: headerStyle }, "Crash Report"),
    React.createElement("div", { style: { fontSize:"13px", color:C.textLight } }, "Loading..."),
  );
}

